<div class="ui-ms">
  <!-- Cabecera: label + badge -->
  @if (showLabel()) {
    <div class="ui-ms__header">
      <label class="ui-ms__label" [attr.for]="triggerId()">{{ label() }}</label>
      @if (showRequirement()) {
        <span
          class="ui-ms__requirement"
          [class.ui-ms__requirement--error]="invalid() && required()"
          aria-hidden="true"
        >
          {{ requirementText() }}
        </span>
      }
    </div>
  }

  <!-- Trigger -->
  <button
    #triggerBtn
    type="button"
    class="ui-ms__trigger"
    [id]="triggerId()"
    [class.ui-ms__trigger--open]="isOpen()"
    [class.ui-ms__trigger--invalid]="invalid()"
    [class.ui-ms__trigger--disabled]="disabled()"
    [class.ui-ms__trigger--compact]="!showLabel()"
    [disabled]="disabled()"
    [attr.aria-haspopup]="'listbox'"
    [attr.aria-expanded]="isOpen()"
    [attr.aria-controls]="listboxId()"
    [attr.aria-required]="required() ? 'true' : null"
    [attr.aria-invalid]="invalid() ? 'true' : null"
    [attr.aria-describedby]="ariaDescribedBy()"
    (click)="toggle()"
    (keydown)="onTriggerKeydown($event)"
  >
    <!-- Sin selección: placeholder -->
    @if (selected().length === 0) {
      <span class="ui-ms__placeholder">{{ placeholder() }}</span>
    } @else {
      <!-- Chips visibles -->
      <span class="ui-ms__chips">
        @for (chip of visibleChips(); track chip.id) {
          <span class="ui-ms__chip">
            <span class="ui-ms__chip-label">{{ chip.label }}</span>
            <span
              class="ui-ms__chip-remove"
              role="button"
              [attr.aria-label]="'Quitar ' + chip.label"
              tabindex="0"
              (click)="removeChip(chip, $event)"
              (keydown.enter)="removeChip(chip, $event)"
              (keydown.space)="removeChip(chip, $event)"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </span>
          </span>
        }
        <!-- Badge de excedente -->
        @if (hiddenCount() > 0) {
          <span class="ui-ms__chip ui-ms__chip--count">+{{ hiddenCount() }}</span>
        }
      </span>
    }

    <!-- Acciones del trigger -->
    <span class="ui-ms__trigger-actions">
      @if (selected().length > 0 && !disabled()) {
        <span
          class="ui-ms__clear"
          role="button"
          aria-label="Limpiar selección"
          tabindex="0"
          (click)="clearAll($event)"
          (keydown.enter)="clearAll($event)"
          (keydown.space)="clearAll($event)"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </span>
      }
      <span
        class="ui-ms__chevron"
        [class.ui-ms__chevron--open]="isOpen()"
        aria-hidden="true"
      >
        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
          <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
    </span>
  </button>

  <!-- Panel dropdown (position:fixed) -->
  @if (isOpen() && panelRect()) {
    <div
      class="ui-ms__panel"
      [id]="listboxId()"
      role="listbox"
      aria-multiselectable="true"
      [attr.aria-label]="label()"
      [style.top.px]="panelRect()!.top"
      [style.left.px]="panelRect()!.left"
      [style.min-width.px]="panelRect()!.width"
      [style.width.px]="fitPanelToContent() ? null : panelRect()!.width"
    >
      <!-- Búsqueda -->
      <div class="ui-ms__search-wrap">
        <span class="ui-ms__search-icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" stroke-width="1.5"/>
            <path d="M10.5 10.5L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </span>
        <input
          #searchInput
          class="ui-ms__search-input"
          type="text"
          role="combobox"
          [attr.aria-expanded]="isOpen()"
          [attr.aria-controls]="listboxId()"
          [attr.aria-activedescendant]="activeOptionId()"
          [placeholder]="searchPlaceholder()"
          [ngModel]="searchQuery()"
          (ngModelChange)="onSearch($event)"
          (click)="$event.stopPropagation()"
          (keydown)="onSearchKeydown($event)"
          autocomplete="off"
        />
      </div>

      <!-- Seleccionar todo / Limpiar todo -->
      <div class="ui-ms__panel-actions">
        <button
          type="button"
          class="ui-ms__action-btn"
          (click)="toggleAll()"
        >
          {{ allSelected() ? 'Limpiar todo' : 'Seleccionar todo' }}
        </button>
        @if (selected().length > 0) {
          <span class="ui-ms__panel-count">{{ selected().length }} seleccionado{{ selected().length !== 1 ? 's' : '' }}</span>
        }
      </div>

      <!-- Lista -->
      <ul class="ui-ms__list" role="presentation">
        @for (option of filteredOptions(); track option.id; let i = $index) {
          <li
            [id]="optionId(i)"
            class="ui-ms__option"
            role="option"
            [class.ui-ms__option--selected]="isSelected(option)"
            [class.ui-ms__option--active]="activeIndex() === i"
            [class.ui-ms__option--disabled]="option.disabled ?? false"
            [attr.aria-selected]="isSelected(option)"
            [attr.aria-disabled]="option.disabled ? 'true' : null"
            (click)="toggleOption(option)"
          >
            <!-- Checkbox visual -->
            <span
              class="ui-ms__checkbox"
              [class.ui-ms__checkbox--checked]="isSelected(option)"
              aria-hidden="true"
            >
              @if (isSelected(option)) {
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4l3 3 5-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              }
            </span>
            <span class="ui-ms__option-label">{{ option.label }}</span>
          </li>
        } @empty {
          <li class="ui-ms__empty" role="option" aria-disabled="true">Sin resultados</li>
        }
      </ul>
    </div>
  }

  <!-- Mensaje de error -->
  @if (message() && invalid() && showMessage()) {
    <p
      class="ui-ms__message ui-ms__message--error"
      [id]="messageId()"
      role="alert"
    >
      {{ message() }}
    </p>
  }
</div>
